import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  PhoneOff,
  Heart,
  XCircle,
  Calendar,
  FileText,
  Trophy,
  X,
} from 'lucide-react';

export type ActionOutcome = 
  | 'no_answer'
  | 'interested'
  | 'not_fit'
  | 'meeting_set'
  | 'proposal_sent'
  | 'won'
  | 'lost';

interface ActionOutcomeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadName: string;
  onSelect: (outcome: ActionOutcome) => void;
}

const outcomeOptions: Array<{
  value: ActionOutcome;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}> = [
  { value: 'no_answer', label: 'No answer', icon: PhoneOff, color: 'text-muted-foreground' },
  { value: 'interested', label: 'Interested', icon: Heart, color: 'text-primary' },
  { value: 'not_fit', label: 'Not fit', icon: XCircle, color: 'text-destructive' },
  { value: 'meeting_set', label: 'Meeting set', icon: Calendar, color: 'text-blue-500' },
  { value: 'proposal_sent', label: 'Proposal sent', icon: FileText, color: 'text-purple-500' },
  { value: 'won', label: 'Won', icon: Trophy, color: 'text-emerald-500' },
  { value: 'lost', label: 'Lost', icon: X, color: 'text-slate-500' },
];

export function ActionOutcomeModal({
  open,
  onOpenChange,
  leadName,
  onSelect,
}: ActionOutcomeModalProps) {
  const [selectedOutcome, setSelectedOutcome] = useState<ActionOutcome | null>(null);

  const handleConfirm = () => {
    if (selectedOutcome) {
      onSelect(selectedOutcome);
      setSelectedOutcome(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">What happened?</DialogTitle>
          <p className="text-sm text-muted-foreground">{leadName}</p>
        </DialogHeader>

        <div className="space-y-2 py-4">
          <RadioGroup
            value={selectedOutcome || ''}
            onValueChange={(v) => setSelectedOutcome(v as ActionOutcome)}
            className="space-y-2"
          >
            {outcomeOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedOutcome === option.value;
              
              return (
                <Label
                  key={option.value}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <RadioGroupItem value={option.value} />
                  <Icon className={cn("w-4 h-4", option.color)} />
                  <span className="text-sm font-medium">{option.label}</span>
                </Label>
              );
            })}
          </RadioGroup>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!selectedOutcome}
          >
            Next
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

